// Quattro pagine settings semplici raggruppate (limiti, retention, email, privacy).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api';
import { FormRow, PrimaryButton, SettingsCard, SuccessHint, inputStyle } from './_shared';

// ============================================================================
// Limiti consumo
// ============================================================================

export function LimitsPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });
  const [soft, setSoft] = useState(1000);
  const [hard, setHard] = useState(5000);
  const [alertEmail, setAlertEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const l = settingsQuery.data.settings.limits;
    setSoft(l.monthly_soft);
    setHard(l.monthly_hard);
    setAlertEmail(l.alert_email_recipient ?? '');
  }, [settingsQuery.data]);

  const mutation = useMutation({
    mutationFn: () =>
      api.updateLimits({
        monthly_soft: soft,
        monthly_hard: hard,
        alert_email_recipient: alertEmail || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setShowSuccess(true);
      setError(null);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  // Stima costo medio per encounter (da docs/COSTS.md)
  const avgCostSonnet = 0.05;
  const avgCostHaiku = 0.01;
  const estimatedHardCostSonnet = (hard * avgCostSonnet).toFixed(2);
  const estimatedHardCostHaiku = (hard * avgCostHaiku).toFixed(2);

  return (
    <SettingsCard
      kicker="Cap consumi"
      title="Limiti mensili"
      description="Soft cap = invio email alert. Hard cap = blocco nuove sessioni con HTTP 429."
      rightSlot={<SuccessHint visible={showSuccess} />}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormRow label="Soft cap (alert)" hint={`Default 1000 encounter/mese`}>
            <input
              type="number"
              min={1}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={soft}
              onChange={(e) => setSoft(Number(e.target.value))}
            />
          </FormRow>
          <FormRow label="Hard cap (blocco)" hint={`Default 5000 encounter/mese`}>
            <input
              type="number"
              min={1}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={hard}
              onChange={(e) => setHard(Number(e.target.value))}
            />
          </FormRow>
        </div>

        <FormRow label="Email destinataria alert" hint="Se vuoto, usiamo l'email dell'admin master">
          <input
            type="email"
            placeholder="alert@studiobianchi.it"
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
            value={alertEmail}
            onChange={(e) => setAlertEmail(e.target.value)}
          />
        </FormRow>

        <div
          className="rounded-md border p-3 text-xs"
          style={{ borderColor: 'var(--line)', background: 'var(--soft)', color: 'var(--ink)' }}
        >
          <div className="mb-1 mono" style={{ color: 'var(--mute)' }}>
            Stima costo al raggiungimento hard cap
          </div>
          <div>
            Sonnet 4.6 (~$0.05/encounter): <strong>${estimatedHardCostSonnet}</strong>
            <span className="ms-3">
              Haiku 4.5 (~$0.01/encounter): <strong>${estimatedHardCostHaiku}</strong>
            </span>
          </div>
        </div>

        {error && (
          <div className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        )}

        <PrimaryButton type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Salvo…' : 'Salva limiti'}
        </PrimaryButton>
      </form>
    </SettingsCard>
  );
}

// ============================================================================
// Retention
// ============================================================================

export function RetentionPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });
  const [days, setDays] = useState(30);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQuery.data) setDays(settingsQuery.data.settings.retention_days);
  }, [settingsQuery.data]);

  const mutation = useMutation({
    mutationFn: () => api.updateRetention(days),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setShowSuccess(true);
      setError(null);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  return (
    <SettingsCard
      kicker="Retention"
      title="Conservazione dati"
      description="Le encounter più vecchie vengono cancellate automaticamente. Le encounter contrassegnate come 'clinicamente rilevanti' non sono mai cancellate."
      rightSlot={<SuccessHint visible={showSuccess} />}
    >
      <FormRow label={`Giorni di conservazione (${days})`}>
        <input
          type="range"
          min={7}
          max={365}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="w-full"
        />
        <div className="mt-1 flex justify-between mono text-xs" style={{ color: 'var(--mute)' }}>
          <span>7 giorni</span>
          <span>30</span>
          <span>90</span>
          <span>180</span>
          <span>365</span>
        </div>
      </FormRow>
      {error && (
        <div className="mb-3 text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      )}
      <PrimaryButton onClick={() => mutation.mutate()} disabled={mutation.isPending}>
        {mutation.isPending ? 'Salvo…' : 'Salva retention'}
      </PrimaryButton>
    </SettingsCard>
  );
}

// ============================================================================
// Email & SMTP
// ============================================================================

export function EmailPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [from, setFrom] = useState('');
  const [secure, setSecure] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!settingsQuery.data?.settings.smtp) return;
    const s = settingsQuery.data.settings.smtp;
    setHost(s.host);
    setPort(s.port);
    setUser(s.user ?? '');
    setFrom(s.from);
    setSecure(s.secure);
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateSmtp({
        host,
        port,
        user,
        password: password || undefined,
        from,
        secure,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setPassword('');
      setShowSuccess(true);
      setError(null);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  const testMutation = useMutation({
    mutationFn: () => api.testSmtp(),
    onSuccess: (data) => setTestResult((data.ok ? '✓ ' : '✗ ') + data.message),
    onError: (err) => setTestResult(err instanceof Error ? '✗ ' + err.message : '✗ Errore'),
  });

  const passwordSet = settingsQuery.data?.settings.smtp?.password_set ?? false;

  return (
    <SettingsCard
      kicker="Email"
      title="SMTP per alert e inviti"
      description="Configurazione invio email transazionali (alert cap, invito admin, reset password)."
      rightSlot={<SuccessHint visible={showSuccess} />}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <FormRow label="Host SMTP">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.fastmail.com"
            />
          </FormRow>
          <FormRow label="Porta">
            <input
              type="number"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={port}
              onChange={(e) => setPort(Number(e.target.value))}
            />
          </FormRow>
          <FormRow label="User">
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={user}
              onChange={(e) => setUser(e.target.value)}
            />
          </FormRow>
          <FormRow
            label="Password"
            hint={passwordSet ? 'Già configurata. Lascia vuoto per non modificarla.' : ''}
          >
            <input
              type="password"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={passwordSet ? '••••••••' : ''}
            />
          </FormRow>
          <FormRow label="From address" hint='Es. "Audiq Studio <noreply@studio.it>"'>
            <input
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </FormRow>
          <FormRow label="Secure (TLS)">
            <label className="mt-2 flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
              <input
                type="checkbox"
                checked={secure}
                onChange={(e) => setSecure(e.target.checked)}
              />
              Usa TLS implicito (porta 465)
            </label>
          </FormRow>
        </div>

        {error && (
          <div className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <PrimaryButton type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Salvo…' : 'Salva SMTP'}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
            className="rounded-md border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
          >
            {testMutation.isPending ? 'Invio test…' : 'Invia email di test al mio indirizzo'}
          </button>
          {testResult && (
            <span className="text-xs mono" style={{ color: 'var(--ink)' }}>
              {testResult}
            </span>
          )}
        </div>
      </form>
    </SettingsCard>
  );
}

// ============================================================================
// Privacy
// ============================================================================

export function PrivacyPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });
  const [policyUrl, setPolicyUrl] = useState('');
  const [dpoEmail, setDpoEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const p = settingsQuery.data.settings.privacy;
    setPolicyUrl(p.policy_url ?? '');
    setDpoEmail(p.dpo_email ?? '');
  }, [settingsQuery.data]);

  const mutation = useMutation({
    mutationFn: () =>
      api.updatePrivacy({
        privacy_policy_url: policyUrl || null,
        privacy_dpo_email: dpoEmail || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setShowSuccess(true);
      setError(null);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  return (
    <SettingsCard
      kicker="Privacy"
      title="Privacy e DPO"
      description="Link all'informativa privacy del cliente e contatto del responsabile protezione dati."
      rightSlot={<SuccessHint visible={showSuccess} />}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-3"
      >
        <FormRow
          label="URL informativa privacy"
          hint="Pagina del cliente con la propria privacy policy"
        >
          <input
            type="url"
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
            value={policyUrl}
            onChange={(e) => setPolicyUrl(e.target.value)}
            placeholder="https://studiobianchi.it/privacy"
          />
        </FormRow>
        <FormRow label="Email DPO" hint="Responsabile protezione dati (Data Protection Officer)">
          <input
            type="email"
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={inputStyle()}
            value={dpoEmail}
            onChange={(e) => setDpoEmail(e.target.value)}
            placeholder="dpo@studiobianchi.it"
          />
        </FormRow>
        {error && (
          <div className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        )}
        <PrimaryButton type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Salvo…' : 'Salva privacy'}
        </PrimaryButton>
      </form>
    </SettingsCard>
  );
}
